import { RfpCoveragesResponse, useRfpCoverages } from "@platform/api";
import { FieldsCard, FieldsData } from "@platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { JSX, useCallback, useState } from "react";

import { queryKeys } from "../../../api/rfp/queries";
import { useAnalyticsData } from "../../../contexts/analyticsContext";
import { useQuerySuccessEffect } from "../../../hooks/useQuerySuccessEffect";
import { useRfpDetails } from "../../../hooks/useRfpDetails";
import { sortProductsByCoverage } from "../../../pages/RFP/utils";

import RfpCoverageEditModal from "./rfpCoveragesEditModal";

type Props = { rfpId: string };

function RFPCoveragesCard({ rfpId }: Props): JSX.Element {
  const [isEdit, setIsEdit] = useState(false);
  const { dispatch } = useAnalyticsData();
  const queryClient = useQueryClient();
  const query = useRfpCoverages({ rfpId });

  // Fetch RFP details to get the ecoRfpId for cache invalidation
  const { data: ecoRfpId } = useRfpDetails<string>({
    rfpId,
    select: (data) => data.ecoRfpId,
  });

  const handleAnalytics = useCallback(
    (data: RfpCoveragesResponse) => {
      const currentCovs = data.currentCoverages || [];
      const newCovs = data.newCoverages || [];
      dispatch({
        coverageCurrentNames: currentCovs.map((c) => c.name),
        coverageCurrentCount: currentCovs.length,
        coverageNewNames: newCovs.map((c) => c.name),
        coverageNewCount: newCovs.length,
      });
    },
    [dispatch],
  );

  useQuerySuccessEffect({
    query,
    onSuccess: handleAnalytics,
  });

  const dataFormatted = query.data
    ? mapResponseToFieldsCardData(query.data)
    : null;

  const onClose = (): void => {
    setIsEdit(false);
    queryClient.invalidateQueries({
      queryKey: ["rfp", "coverages", "edit", rfpId],
    });
    if (ecoRfpId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfpCoverages(ecoRfpId),
      });
    }
  };

  return (
    <>
      <FieldsCard
        title="Coverages"
        subtitle={
          dataFormatted?.total
            ? `${dataFormatted?.total} Total Lines of Coverage`
            : null
        }
        status={query.status}
        error={query.error}
        gridItemProps={{
          size: 12,
        }}
        skeleton={{
          count: 2,
          props: {
            width: "50%",
            height: 30,
          },
        }}
        onEdit={() => setIsEdit(true)}
        data={dataFormatted?.fields}
      />
      {isEdit && (
        <RfpCoverageEditModal
          onClose={onClose}
          currentData={query.data}
          rfpId={rfpId}
          ecoRfpId={ecoRfpId}
        />
      )}
    </>
  );
}

type Data = { total: number; fields: FieldsData[] };

export function mapResponseToFieldsCardData(resp: RfpCoveragesResponse): Data {
  return [
    {
      title: "Current Coverages to Quote",
      coverages: sortProductsByCoverage(
        resp.currentCoverages,
      ) as RfpCoveragesResponse["currentCoverages"],
    },
    {
      title: "New Coverages to Quote",
      coverages: sortProductsByCoverage(
        resp.newCoverages,
      ) as RfpCoveragesResponse["newCoverages"],
    },
  ].reduce<{ total: number; fields: FieldsData[] }>(
    (acc, { title, coverages }) => {
      if (coverages) {
        acc.total += coverages.length;
        acc.fields.push({
          label: title,
          value: coverages.map((coverage) => coverage.name).join("    "),
        });
      }
      return acc;
    },
    { total: 0, fields: [] },
  );
}

export default RFPCoveragesCard;
